import { Injectable, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { isArray, isNumber, isObject } from 'util';
import * as moment from 'moment';
import { has } from 'lodash';
import { Subject } from 'rxjs/Subject';

import { RangeDates } from '@gosuite/range-datepicker';

import * as AppStateActions from '../../state-management/actions/app-state';
import * as DisableStateActions from '../../../shared/state-management/actions/disabled';
import * as ValidateActions from '../../../xena/core/state-management/actions/validate';
import * as QueryActions from '../../../xena/core/state-management/actions/query';
import { IpcChannelDataInterface } from './ipc-channel.data.interface';
import { IpcChannelService } from './ipc-channel.service';
import { State } from '../../state-management/reducers';
import { Go } from '../../state-management/actions/router';
import { InitialSearchformPaState, SearchformPaModel } from '../../../xena/core/shared/models/searchform/searchform-pa.model';
import { InitialSearchformNhState, SearchformNhModel } from '../../../xena/core/shared/models/searchform/searchform-nh.model';
import { InitialSearchformNfState, SearchformNfModel } from '../../../xena/core/shared/models/searchform/searchform-nf.model';
import { UpdateSearchformNh } from '../../../xena/core/state-management/actions/searchform-nh';
import { UpdateSearchformNf } from '../../../xena/core/state-management/actions/searchform-nf';
import { UpdateSearchformPa } from '../../../xena/core/state-management/actions/searchform-pa';
import { XenaExeQueryModel } from './submit-query.model';
import { SwitchSearchType } from '../../../xena/core/state-management/actions/settings';
import { SubmitQuery } from '../../../xena/core/state-management/actions/query';
import { createQueryFromSearchformNfModel, createQueryFromSearchformNhModel, createQueryFromSearchformPaModel } from '../../../xena/core/shared/models/query/query.model';


@Injectable()
export class DefaultIpcChannelListeners implements OnDestroy {
	private _onComponentDestroy$: Subject<boolean> = new Subject<boolean>();

	public constructor(
		private _store: Store<State>,
		private _ipc: IpcChannelService
	) {
		this._ipc.data$.takeUntil(this._onComponentDestroy$).subscribe(e => this._handleIpcChannelEvent(e));
	}

	public ngOnDestroy(): void {
		this._onComponentDestroy$.next(true);
	}

	protected XENA(event: IpcChannelDataInterface) {
		switch (event.payload) {
			case 'reset':
				this._store.dispatch(new QueryActions.ResetQuery());
				break;

			case 'standalone':
				this._store.dispatch(new DisableStateActions.DisableAction(['neo']));
				this._store.dispatch(new DisableStateActions.EnableAction(['xena']));
		}
	}

	protected CRS(event: IpcChannelDataInterface) {
		this._store.dispatch(new ValidateActions.SendXmlToCrs(decodeURIComponent(event.payload)));
	}

	protected NEO(event: IpcChannelDataInterface) {
		switch (event.payload) {
			case 'open':
				this._store.dispatch(new AppStateActions.OpenNeoFrame());
				break;

			case 'close':
				this._store.dispatch(new AppStateActions.CloseNeoFrame());
				break;

			case 'standalone':
				this._store.dispatch(new DisableStateActions.DisableAction(['xena']));
				this._store.dispatch(new DisableStateActions.EnableAction(['neo']));
		}
	}

	private _handleIpcChannelEvent(e): void {
		if (e['type']) {
			if (e['type'] === '[XENA] SUBMIT_QUERY' && isObject(e['payload'])) {
				switch (e['payload']['type']) {
					case 'NH':
						const payloadNh = this._queryToUpdateSearchformNh(e['payload']);
						this._store.dispatch(new SwitchSearchType('NH'));
						this._store.dispatch(new UpdateSearchformNh(payloadNh));
						this._store.dispatch(new Go({path: ['/xena']}));
						this._store.dispatch(new AppStateActions.CloseNeoFrame());
						setTimeout(() => this._store.dispatch(new SubmitQuery(createQueryFromSearchformNhModel(payloadNh))), 1000);
						break;

					case 'NF':
						const payloadNf = this._queryToUpdateSearchformNf(e['payload']);
						this._store.dispatch(new SwitchSearchType('NF'));
						this._store.dispatch(new UpdateSearchformNf(payloadNf));
						this._store.dispatch(new Go({path: ['/xena']}));
						this._store.dispatch(new AppStateActions.CloseNeoFrame());
						setTimeout(() => this._store.dispatch(new SubmitQuery(createQueryFromSearchformNfModel(payloadNf))), 1000);
						break;

					case 'PA':
					default:
						const payloadPa = this._queryToUpdateSearchformPa(e['payload']);
						this._store.dispatch(new SwitchSearchType('PA'));
						this._store.dispatch(new UpdateSearchformPa(payloadPa));
						this._store.dispatch(new Go({path: ['/xena']}));
						this._store.dispatch(new AppStateActions.CloseNeoFrame());
						setTimeout(() => this._store.dispatch(new SubmitQuery(createQueryFromSearchformPaModel(payloadPa))), 1000);
						break;
				}
			} else {
				this._store.dispatch((e as any));
			}
		}

		if (this[e.action]) {
			this[e.action](e);
		}
	}

	private _queryToUpdateSearchformPa(query: XenaExeQueryModel): SearchformPaModel {
		if (isObject(query)) {
			return {
				departureAirports: isArray(query.departure) ? query.departure : [],
				destinationAirports: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('DST-') !== -1)
					: [],
				destinationRegions: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('BT10-') !== -1 || tag.value.indexOf('XT10-') !== -1)
					: [],
				destinationCities: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('BT09-') !== -1)
					: [],
				hotels: isArray(query.hotelName) ? query.hotelName : [],
				dates: has(query, 'date.startDate') && has(query, 'date.endDate')
					? new RangeDates(moment(query.date.startDate).toDate(), moment(query.date.endDate).toDate())
					: new RangeDates(InitialSearchformPaState.dates.fromDate, InitialSearchformPaState.dates.toDate),
				minDuration: isNumber(query.minDuration) ? query.minDuration : InitialSearchformPaState.minDuration,
				maxDuration: isNumber(query.maxDuration) ? query.maxDuration : InitialSearchformPaState.maxDuration,
				adultsNumber: isNumber(query.adultCount) ? query.adultCount : InitialSearchformPaState.adultsNumber,
				childAge1: isNumber(query.childAge1) ? query.childAge1 : InitialSearchformPaState.childAge1,
				childAge2: isNumber(query.childAge2) ? query.childAge2 : InitialSearchformPaState.childAge2,
				childAge3: isNumber(query.childAge3) ? query.childAge3 : InitialSearchformPaState.childAge3,
				childAge4: isNumber(query.childAge4) ? query.childAge4 : InitialSearchformPaState.childAge4,
				category: isNumber(query.category) ? query.category : InitialSearchformPaState.category,
				rating: isNumber(query.rating) ? query.rating : InitialSearchformPaState.rating,
				boardTypes: isArray(query.board) ? query.board : [],
				roomTypes: isArray(query.roomType) ? query.roomType : [],
				maxPrice: isNumber(query.maxPrice) ? query.maxPrice : InitialSearchformPaState.maxPrice,
				tourOperators: isArray(query.operator) ? query.operator : [],
				disableDynamicTourOperators: query.disableDynamicTourOperators || InitialSearchformPaState.disableDynamicTourOperators,
				negateTourOperatorsSelect: query.negateTourOperatorsSelect || InitialSearchformPaState.negateTourOperatorsSelect,
				nonStopFlight: query.nonStopFlight || InitialSearchformPaState.nonStopFlight,
				railAndFly: query.railAndFly || InitialSearchformPaState.railAndFly,
				seaView: query.seaView || InitialSearchformPaState.seaView,
				hygiene: query.hygiene || InitialSearchformPaState.hygiene,
				topsFlops: isArray(query.topsFlops) ? query.topsFlops : [],
			};
		}

		return null;
	}

	private _queryToUpdateSearchformNh(query: XenaExeQueryModel): SearchformNhModel {
		if (isObject(query)) {
			return {
				destinationAirports: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('DST-') !== -1)
					: [],
				destinationRegions: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('BT10-') !== -1 || tag.value.indexOf('XT10-') !== -1)
					: [],
				destinationCities: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('BT09-') !== -1)
					: [],
				hotels: isArray(query.hotelName) ? query.hotelName : [],
				dates: has(query, 'date.startDate') && has(query, 'date.endDate')
					? new RangeDates(moment(query.date.startDate).toDate(), moment(query.date.endDate).toDate())
					: new RangeDates(InitialSearchformNhState.dates.fromDate, InitialSearchformNhState.dates.toDate),
				minDuration: isNumber(query.minDuration) ? query.minDuration : InitialSearchformNhState.minDuration,
				maxDuration: isNumber(query.maxDuration) ? query.maxDuration : InitialSearchformNhState.maxDuration,
				adultsNumber: isNumber(query.adultCount) ? query.adultCount : InitialSearchformNhState.adultsNumber,
				childAge1: isNumber(query.childAge1) ? query.childAge1 : InitialSearchformNhState.childAge1,
				childAge2: isNumber(query.childAge2) ? query.childAge2 : InitialSearchformNhState.childAge2,
				childAge3: isNumber(query.childAge3) ? query.childAge3 : InitialSearchformNhState.childAge3,
				childAge4: isNumber(query.childAge4) ? query.childAge4 : InitialSearchformNhState.childAge4,
				category: isNumber(query.category) ? query.category : InitialSearchformNhState.category,
				rating: isNumber(query.rating) ? query.rating : InitialSearchformNhState.rating,
				boardTypes: isArray(query.board) ? query.board : [],
				roomTypes: isArray(query.roomType) ? query.roomType : [],
				maxPrice: isNumber(query.maxPrice) ? query.maxPrice : InitialSearchformNhState.maxPrice,
				tourOperators: isArray(query.operator) ? query.operator : [],
				negateTourOperatorsSelect: query.negateTourOperatorsSelect || InitialSearchformNhState.negateTourOperatorsSelect,
				seaView: query.seaView || InitialSearchformNhState.seaView,
				hygiene: query.hygiene || InitialSearchformNhState.hygiene,
				topsFlops: isArray(query.topsFlops) ? query.topsFlops : []
			};
		}

		return null;
	}

	private _queryToUpdateSearchformNf(query: XenaExeQueryModel): SearchformNfModel {
		if (isObject(query)) {
			return {
				departureAirports: isArray(query.departure) ? query.departure : [],
				destinationAirports: isArray(query.destination)
					? query.destination.filter(tag => tag.value.indexOf('DST-') !== -1)
					: [],
				dates: has(query, 'date.startDate') && has(query, 'date.endDate')
					? new RangeDates(moment(query.date.startDate).toDate(), moment(query.date.endDate).toDate())
					: new RangeDates(InitialSearchformNfState.dates.fromDate, InitialSearchformNfState.dates.toDate),
				minDuration: isNumber(query.minDuration) ? query.minDuration : InitialSearchformNfState.minDuration,
				maxDuration: isNumber(query.maxDuration) ? query.maxDuration : InitialSearchformNfState.maxDuration,
				outboundAndReturnFlight: query.twoWay || InitialSearchformNfState.outboundAndReturnFlight,
				adultsNumber: isNumber(query.adultCount) ? query.adultCount : InitialSearchformNfState.adultsNumber,
				childAge1: isNumber(query.childAge1) ? query.childAge1 : InitialSearchformNfState.childAge1,
				childAge2: isNumber(query.childAge2) ? query.childAge2 : InitialSearchformNfState.childAge2,
				childAge3: isNumber(query.childAge3) ? query.childAge3 : InitialSearchformNfState.childAge3,
				childAge4: isNumber(query.childAge4) ? query.childAge4 : InitialSearchformNfState.childAge4,
				maxPrice: isNumber(query.maxPrice) ? query.maxPrice : InitialSearchformNfState.maxPrice,
				tourOperators: isArray(query.operator) ? query.operator : [],
				negateTourOperatorsSelect: query.negateTourOperatorsSelect || InitialSearchformNfState.negateTourOperatorsSelect
			};
		}

		return null;
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/ipc-channel/default-ipc-channel-listeners.ts